/* eslint-disable complexity */
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import {
  CustomTimeframeFilter,
  LocalFilterSection,
} from 'src/components/molecules';
import { SlideOut } from 'src/components/molecules/SlideOut/SlideOut';
import { useLocalFilters } from 'src/hooks';
import * as Types from 'src/types';
import { CHART_IDS, trackChartFiltered } from 'src/utils/analytics';
import { LocationFilterTitle } from './LocalFilter.styles';
import {
  LocalFilterOptions,
  ResetFilterOptions,
  SaveFilterParams,
} from './LocalFilter.types';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Intercom: any;
  }
}
export interface Props {
  filters: LocalFilterOptions;
  resetFilters?: ResetFilterOptions;
  closeFilters: () => void;
  saveFilters: (params: SaveFilterParams) => void;
  isOpen: boolean;
  'data-testid'?: string;
  dashboard: Types.InsightsDashboard;
  chartId?: CHART_IDS;
  originalSelectedLocationFilter?: Types.GlobalFilterLocation;
  selectedLocationFilter?: Types.GlobalFilterLocation;
  setSelectedLocationFilter?: (location: Types.GlobalFilterLocation) => void;
}

export const LocalFilter = ({
  filters,
  resetFilters,
  closeFilters,
  saveFilters,
  isOpen,
  originalSelectedLocationFilter = Types.GlobalFilterLocation.SITES,
  selectedLocationFilter = Types.GlobalFilterLocation.SITES,
  setSelectedLocationFilter,
  ...props
}: Props) => {
  const {
    selectedSites,
    selectedCountries,
    selectedDepartments,
    setSelectedDepartments,
    selectedCategories,
    setSelectedCategories,
    selectedTimeframe,
    timeframeState,
    applyButtonDisabled,
    selectFilterOnChange,
    timeFilterOnChange,
    clearFilters,
    onSelectAll,
    selectedDepartmentsChanged,
    selectedCategoriesChanged,
    selectedTimeframeChanged,
    selectedLocationsChanged,
    locationFilter,
    selectedLocationFilterState,
    setSelectedLocationFilterOptions,
    selectedLocationLimit,
    selectedLocationOptions,
    locationFilterOption,
  } = useLocalFilters({
    filters,
    resetFilters,
    isOpen,
    selectedLocationFilter,
    originalSelectedLocationFilter,
    setSelectedLocationFilter,
  });
  const organisation = useSelector((state: Types.State) => state.organisation);
  const features = useSelector((state: Types.State) => state.features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const locationFilterTitle = 'Location';
  const filtersChanged: string[] = [];
  selectedLocationsChanged &&
    filtersChanged.push(
      selectedLocationFilter === Types.GlobalFilterLocation.SITES
        ? 'site'
        : 'country',
    );
  selectedDepartmentsChanged && filtersChanged.push('department');
  selectedCategoriesChanged && filtersChanged.push('category');
  selectedTimeframeChanged && filtersChanged.push('timeframe');
  const handleApplyFiltersClick = () => {
    saveFilters({
      selectedSites,
      selectedCountries,
      selectedDepartments,
      selectedCategories,
      selectedTimeframe,
      selectedLocationFilter,
    });
    if (organisation.organisation && segmentEnabled && props.chartId) {
      trackChartFiltered(
        organisation.organisation.id,
        props.dashboard,
        props.chartId,
        filtersChanged,
      );
    }
  };

  useEffect(() => {
    if (window.Intercom) {
      isOpen
        ? window.Intercom('update', { hide_default_launcher: true })
        : window.Intercom('update', { hide_default_launcher: false });
    }
  }, [isOpen]);

  const renderFiltersTime = () => {
    if (filters.time) {
      return (
        <div style={{ marginTop: '40px' }} data-testid="time-section">
          <CustomTimeframeFilter
            timeframe={selectedTimeframe ?? filters.time.timeframe}
            onChange={timeFilterOnChange}
            oldestSelectableYear={filters.time.oldestSelectableYear}
            state={timeframeState}
            hasBottomDivider={Boolean(filters.department || filters.category)}
          />
        </div>
      );
    }
  };

  const renderFiltersDepartment = () => {
    if (filters.department) {
      return (
        <LocalFilterSection
          title="Department"
          type="checkbox"
          options={filters.department.options}
          selectedOptions={selectedDepartments ?? []}
          onChange={selectFilterOnChange(
            selectedDepartments ?? [],
            setSelectedDepartments,
            filters.department.limit,
          )}
          onSelectAll={onSelectAll(
            'department',
            selectedDepartments ?? [],
            setSelectedDepartments,
          )}
          hasBottomDivider={Boolean(filters.category)}
          limit={filters.department.limit}
          limitMessage={
            filters.department.limit &&
            filters.department.options.length > filters.department.limit
              ? `You may choose up to ${filters.department.limit} departments`
              : ''
          }
          filtersIsOpen={isOpen}
          data-testid="department-section"
          tooltipText={
            filters.department.limit &&
            filters.department.options.length > filters.department.limit
              ? `The top ${filters.department.limit} departments have been automatically selected for you`
              : ''
          }
        />
      );
    }
  };

  const renderFiltersCategory = () => {
    if (filters.category) {
      return (
        <LocalFilterSection
          title="Category"
          type="checkbox"
          options={filters.category.options}
          selectedOptions={selectedCategories ?? []}
          onChange={selectFilterOnChange(
            selectedCategories ?? [],
            setSelectedCategories,
            filters.category.limit,
          )}
          onSelectAll={onSelectAll(
            'category',
            selectedCategories ?? [],
            setSelectedCategories,
          )}
          limit={filters.category.limit}
          limitMessage={
            filters.category.limit &&
            filters.category.options.length > filters.category.limit
              ? `You may choose up to ${filters.category.limit} categories`
              : ''
          }
          filtersIsOpen={isOpen}
          data-testid="category-section"
        />
      );
    }
  };

  const FiltersContainer = () => (
    <>
      {locationFilter && <LocationFilterTitle>Location</LocationFilterTitle>}
      {locationFilter && (
        <LocalFilterSection
          title={locationFilterTitle}
          type="box"
          options={locationFilter.options.map((o) => ({ label: o }))}
          selectedOptions={selectedLocationFilterState ?? []}
          onChange={selectFilterOnChange(
            selectedLocationFilterState ?? [],
            setSelectedLocationFilterOptions,
            selectedLocationLimit,
          )}
          onSelectAll={onSelectAll(
            locationFilterOption,
            selectedLocationFilterState ?? [],
            setSelectedLocationFilterOptions,
          )}
          hasBottomDivider={Boolean(
            filters.time || filters.department || filters.category,
          )}
          limit={selectedLocationLimit}
          limitMessage={
            selectedLocationLimit &&
            selectedLocationOptions &&
            selectedLocationOptions?.length > selectedLocationLimit
              ? `You may choose up to ${selectedLocationLimit} locations`
              : ''
          }
          filtersIsOpen={isOpen}
          isGroupedLocationFilter
          selectedGroupedLocationFilter={selectedLocationFilter}
          setSelectedGroupedLocationFilter={setSelectedLocationFilter}
          data-testid="location-section"
        />
      )}
      {renderFiltersTime()}
      {renderFiltersDepartment()}
      {renderFiltersCategory()}
    </>
  );

  return (
    <SlideOut
      isOpen={isOpen}
      title={'Filters'}
      onClose={closeFilters}
      onCancel={clearFilters}
      onConfirm={handleApplyFiltersClick}
      applyButtonDisabled={applyButtonDisabled}
      cancelButtonText={'Reset filters'}
      cancelButtonDataTestid={'reset-filters'}
      confirmButtonText={'Apply filters'}
      confirmButtonDataTestid={'apply-filters'}
      {...props}
    >
      <FiltersContainer />
    </SlideOut>
  );
};
